import { zodResolver } from "@hookform/resolvers/zod";
import { Alert, Button, Form, FormItem, Text } from "~/components/ui";
import { BookPlus } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FunctionComponent } from "react";
import { useForm, type FieldErrors } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { AddLexiconTermData } from "@noa/api-core";
import { useCreateLexiconConcept, useGetLexiconSubjects } from "@noa/api-client";
import { useCurrentLanguage } from "@noa/i18n";
import type { LexiconSubject } from "~/common/lexicon-subject";
import { LexiconConceptFields } from "~/components/lexicon/lexicon-concept-fields";
import {
  firstTabWithError,
  fullLexicalConceptFormToRequestBody,
  isAcronym,
  lexiconConceptSchema,
  subjectId,
} from "~/utils/lexicon";
import type { LexiconConceptForm } from "~/utils/lexicon";
import type { ActionFormProps } from "../registry";

/**
 * In-bubble form for adding a term the agent could not interpret.
 *
 * Submits straight from the chat rather than opening the lexicon modal: the
 * agent started this flow, so finishing it shouldn't move the user elsewhere.
 * Fields come from `LexiconConceptFields`, the same body both lexicon-screen
 * modals render, so the three surfaces cannot drift apart.
 *
 * The tab is controlled here so a failed submit can reveal the language tab
 * holding the error — without that, half the required fields are hidden and
 * the button looks dead.
 */
export const AddLexiconTermForm: FunctionComponent<ActionFormProps<AddLexiconTermData>> = ({
  data,
  onChange,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const { t } = useTranslation("lexicon");
  const appLanguage = useCurrentLanguage();
  const { subjects: apiSubjects, isLoadingSubjects, isError: subjectsFailed } = useGetLexiconSubjects();
  const { mutate: createLexiconConcept, isPending } = useCreateLexiconConcept();
  const [activeTab, setActiveTab] = useState<string>(appLanguage);
  const [submitError, setSubmitError] = useState<string>();
  const hasSeeded = useRef(false);

  // Both translations are required to build the request body, so a subject
  // missing one can only fail at submit time — leave it out of the options
  // rather than let the user pick a dead end.
  //
  // Sorted in the app language like the lexicon screen's list: the API returns
  // subjects ordered by their English label, so skipping this leaves the French
  // dropdown reading as unsorted.
  const subjects: LexiconSubject[] = useMemo(
    () =>
      apiSubjects
        .filter((s) => s.fr && s.en)
        .map((s) => ({ ...s, id: subjectId(s) }))
        .sort((a, b) => (a[appLanguage] ?? "").localeCompare(b[appLanguage] ?? "")),
    [apiSubjects, appLanguage]
  );

  // A concept must be filed under an existing subject, and this card cannot
  // create one — only the lexicon screen can. So an empty list is a dead end,
  // and the user deserves to know that before filling five fields rather than
  // after: the subject Select is simply empty, and `firstTabWithError` ignores
  // `subject_id` (it renders on both tabs), so a blocked submit would otherwise
  // report nothing at all.
  //
  // Empty covers three causes — a lexicon with no concepts yet, a failed fetch,
  // and every subject being half-translated — which differ for us but not for
  // the user: in each case the fix is elsewhere.
  const blockedReason = isLoadingSubjects
    ? undefined
    : subjectsFailed
      ? t("errors.subjectsUnavailable")
      : subjects.length === 0
        ? t("errors.noSubjectsYet")
        : undefined;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LexiconConceptForm>({ resolver: zodResolver(lexiconConceptSchema) });

  // Seed from what the agent reported. An acronym fills the acronym fields and
  // leaves the term for the user to spell out; anything else is the term itself.
  // The context sentence seeds both descriptions — seeding only French would
  // leave the English tab failing validation on a field the user never saw.
  //
  // Seeds once, guarded by a ref rather than by its dependencies: on success the
  // submit writes `data.term` so the card's line can name the submitted term, and
  // without this guard that write re-runs the effect — re-seeding the fields from
  // the user's own term while the card is still showing the form.
  useEffect(() => {
    if (!data.term || hasSeeded.current) return;
    hasSeeded.current = true;

    // The term fields are seeded either way, because they are required and a
    // misclassification that leaves them blank fails validation on fields the
    // user was never prompted to fill. An acronym additionally seeds the acronym
    // fields — so a wrong guess costs the user an edit, never a dead end.
    setValue("term_fr", data.term);
    setValue("term_en", data.term);

    if (isAcronym(data.term)) {
      setValue("acronyms_fr", [data.term]);
      setValue("acronyms_en", [data.term]);
    }

    if (data.context) {
      setValue("description_fr", data.context);
      setValue("description_en", data.context);
    }
  }, [data.term, data.context, setValue]);

  const submitConcept = (values: LexiconConceptForm) => {
    setSubmitError(undefined);

    const body = fullLexicalConceptFormToRequestBody(values, subjects);

    // Only reachable if a subject lost a translation between load and submit;
    // say so rather than returning silently, which reads as a dead button.
    if (!body) {
      setSubmitError(t("errors.subjectNotTranslated"));
      return;
    }

    createLexiconConcept(body, {
      onSuccess: () => {
        // The card's success line interpolates `data.term`, which is still only
        // the agent's original guess — on the acronym path the user spells out a
        // term of their own, so reporting the guess names something they never
        // submitted. Sync it to what was actually persisted.
        //
        // Only on success. `data.term` is the agent's statement of what it could
        // not interpret, and the card's prompt renders it: overwriting it before
        // the request settles left a failed submit quoting the user's own term
        // back at them as the thing the agent did not understand.
        onChange({ ...data, term: appLanguage === "fr" ? values.term_fr : values.term_en });
        // Hands the card its terminal "success" state; the registry's submitHandler
        // is a no-op because the mutation is owned here, where the form lives.
        onSubmit();
      },
      // The card's own error branch is driven by submitHandler throwing, which
      // this action never does — so surface the failure here, next to the form,
      // instead of leaving the card looking untouched.
      onError: () => setSubmitError(t("errors.submitFailed")),
    });
  };

  // Validation errors on a hidden tab are invisible, so a plain submit looks
  // like a dead button. Reveal the offending tab instead.
  //
  // Reads the errors react-hook-form hands `onInvalid`, not the `formState` ones
  // above: `onInvalid` runs in the same tick that populates them, before React
  // re-renders, so the closed-over copy is still empty on the first failed
  // submit — which is the one that has to work.
  const revealInvalidTab = (submitErrors: FieldErrors<LexiconConceptForm>) => {
    const target = firstTabWithError(submitErrors, activeTab === "en" ? "en" : "fr");
    if (target) setActiveTab(target);
  };

  const pending = isSubmitting || isPending;

  // Submitting without a usable subject list fails the lookup and reports "this
  // subject has no translation" — blaming the data for a loading state, a failed
  // fetch, or a lexicon that has no subjects yet.
  const canSubmit = !pending && !isLoadingSubjects && !blockedReason;

  return (
    <div className="action-card-lexicon">
      <div className="flex items-start gap-2 mb-3">
        <BookPlus size={18} className="mt-0.5 shrink-0" />
        <Text>{t("actionCard.termNotFound", { term: data.term })}</Text>
      </div>
      <Form size="small" onSubmit={handleSubmit(submitConcept, revealInvalidTab)}>
        <LexiconConceptFields
          control={control}
          errors={errors}
          language={appLanguage}
          subjects={subjects}
          size="small"
          activeKey={activeTab}
          onTabChange={setActiveTab}
          autoFocusSubject
        />
        {blockedReason && <Alert type="warning" message={blockedReason} showIcon className="mb-2" />}
        {submitError && <Alert type="error" message={submitError} showIcon className="mb-2" />}
        <FormItem className="mb-0 mt-2">
          <div className="inline-flex items-center gap-2">
            <Button variant="primary" type="submit" loading={pending} disabled={!canSubmit}>
              {t("actionCard.addToLexicon")}
            </Button>
            <Button onClick={onCancel} disabled={pending}>
              {t("actionCard.ignore")}
            </Button>
          </div>
        </FormItem>
      </Form>
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components -- action card form + submit handler co-located per action-cards design spec
export async function submitAddLexiconTerm(): Promise<void> {
  // No-op: the form above owns the create mutation, so by the time the card's
  // submitHandler runs the concept is already persisted. Kept because the
  // registry contract requires a handler per action type.
}
